import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link, useSearchParams } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import ExtensaoFilterBar from '../../components/ExtensaoFilterBar';
import ConfirmModal from '../../components/ConfirmModal';
import { fetchExtensoes } from '../../services/extensaoService';
import { useAuth } from '../../contexts/AuthContext';
import Cookies from 'js-cookie';

const columns = [
  { Header: 'ID', accessor: 'idExtensao' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'Cidade', accessor: 'cidade' },
  { Header: 'UF', accessor: 'uf' },
  { Header: 'Telefone', accessor: 'telefone1' },
  { Header: 'Email', accessor: 'email' },
  { Header: 'Igreja', accessor: 'nomeIgreja' },
  { Header: 'Pastor Presidente', accessor: 'nomePastorPresidente' },
  { Header: 'Estrangeira', accessor: 'estrangeira', Cell: (value) => value ? 'Sim' : 'Não' },
  { Header: 'País', accessor: 'pais' },
  { Header: 'Status', accessor: 'status' },
];

const pageSizes = [5, 10, 20];

const ExtensoesList = () => {
  const { hasPermission } = useAuth();
  // Permite chegar nesta tela com filtros pré-aplicados via querystring
  // (ex.: /extensoes?status=Ativa — drill-down do KPI "Extensões ativas"
  // do Dashboard Admin).
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    nome: searchParams.get('nome') || '',
    cidade: searchParams.get('cidade') || '',
    uf: searchParams.get('uf') || '',
    status: searchParams.get('status') || '',
    email: searchParams.get('email') || '',
    nomeIgreja: searchParams.get('nomeIgreja') || '',
    nomePastorPresidente: searchParams.get('nomePastorPresidente') || '',
    pais: searchParams.get('pais') || '',
    estrangeira: searchParams.get('estrangeira') || '',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedExtensao, setSelectedExtensao] = useState(null);
  const [extensoes, setExtensoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    const getExtensoes = async () => {
      try {
        const data = await fetchExtensoes();
        setExtensoes(data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    getExtensoes();
  }, []);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtragem (client-side). Defensiva contra valores null/undefined no banco.
  // Usa busca parcial (includes) para texto livre e igualdade exata para
  // campos com poucos valores possíveis (status, estrangeira) — isso evita
  // o bug de "Ativa" também casar com "Inativa" via .includes().
  const matchPartial = (campo, filtro) =>
    !filtro || String(campo || '').toLowerCase().includes(String(filtro).toLowerCase());
  const matchExact = (campo, filtro) =>
    !filtro || String(campo || '').toLowerCase() === String(filtro).toLowerCase();

  const filteredExtensoes = extensoes.filter((extensao) => {
    return (
      matchPartial(extensao.nome, filters.nome) &&
      matchPartial(extensao.cidade, filters.cidade) &&
      matchPartial(extensao.uf, filters.uf) &&
      matchExact(extensao.status, filters.status) &&
      matchPartial(extensao.email, filters.email) &&
      matchPartial(extensao.nomeIgreja, filters.nomeIgreja) &&
      matchPartial(extensao.nomePastorPresidente, filters.nomePastorPresidente) &&
      matchPartial(extensao.pais, filters.pais) &&
      (filters.estrangeira === '' || String(extensao.estrangeira) === filters.estrangeira)
    );
  });

  // Paginação
  const totalPages = Math.ceil(filteredExtensoes.length / pageSize);
  const paginatedExtensoes = filteredExtensoes.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleEdit = (id) => {
    window.location.href = `/extensoes/${id}/edit`;
  };

  const handleDelete = (extensao) => {
    setSelectedExtensao(extensao);
    setShowConfirm(true);
  };

  const handleGerarRelatorio = async () => {
    setLoadingPdf(true);
    try {
      const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
      if (!token) throw new Error('Token de autenticação não encontrado.');
      let baseUrl;
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      } else {
        baseUrl = 'https://api.ibe.elshadaysystems.com.br/api';
      }
      const response = await fetch(`${baseUrl}/extensoes/relatorio`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Erro ao gerar relatório: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-extensoes-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao gerar relatório:', err);
      alert(`Erro ao gerar relatório PDF: ${err.message}`);
    } finally {
      setLoadingPdf(false);
    }
  };

  const confirmDelete = () => {
    // Implementar chamada de exclusão na API
    setShowConfirm(false);
    setSelectedExtensao(null);
  };

  if (loading) return <div>Carregando extensões...</div>;
  if (error) return <div>Erro: {error.message}</div>;
  if (!hasPermission('extensoes.list')) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">Acesso negado: você não tem permissão para visualizar extensões.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Extensões</h2>
            <div className="flex gap-2">
              <button
                onClick={handleGerarRelatorio}
                disabled={loadingPdf}
                className={`px-4 py-2 rounded ${loadingPdf ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white font-semibold shadow focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center gap-2`}
              >
                <FontAwesomeIcon icon={faFilePdf} className={loadingPdf ? 'animate-pulse' : ''} />
                <span>{loadingPdf ? 'Gerando PDF...' : 'Relatório PDF'}</span>
              </button>
              <Link
                to="/extensoes/create"
                className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Nova Extensão</span>
              </Link>
            </div>
          </div>
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          <div className="max-w-full">
            <ExtensaoFilterBar filters={filters} onChange={handleFilterChange} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={filteredExtensoes.length}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'nome') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <Link
                          to={`/extensoes/${row.idExtensao}/edit`}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Link>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={paginatedExtensoes}
            />
          </div>
        </div>
      </div>
      {showConfirm && (
        <ConfirmModal
          title="Confirmar exclusão"
          description={`Deseja realmente excluir a extensão "${selectedExtensao?.nome}"?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirm(false)}
          confirmType="danger"
        />
      )}
    </div>
  );
};

export default ExtensoesList;
